import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axiosConfig';

// MUI
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// React Calendar
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './Calendar.css'; // Fichier de style personnalisé

/**
 * Génère une couleur distincte en utilisant le modèle HSL.
 * @param {number} index - L'indice de la compétition dans la liste.
 * @param {number} total - Le nombre total de compétitions.
 * @returns {string} Une couleur HSL.
 */
const generateColor = (index, total) => {
  const hue = Math.floor((360 / total) * index);
  return `hsl(${hue}, 70%, 50%)`;
};


/**
 * Composant ProfilSchedule 
 * - Affiche le calendrier (react-calendar) positionné à gauche.
 * - Les jours avec match affichent de petites pastilles colorées (une par compétition).
 * - Les informations du match du jour sont affichées dans des cards esthétiques.
 * - En bas, une card présente les 5 prochains matchs.
 * - Pour les matchs passés, affiche les statistiques du joueur
 */
const ProfilSchedule = ({ playerId }) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [schedule, setSchedule] = useState([]); // Tous les matchs
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [matchStats, setMatchStats] = useState({}); // Stockage des stats par match
  const [loadingStats, setLoadingStats] = useState(false);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  // Récupération des données depuis le backend
  useEffect(() => {
    const fetchSchedule = async () => {
      if (!playerId) return;
      setIsLoading(true);
      try {
        const response = await api.get(
          `/api/player-stats/${playerId}/schedule`
        );
        setSchedule(response.data);
      } catch (err) {
        console.error('Error al obtener el calendario:', err);
        setError(t('schedule.errorFetchingSchedule'));
      } finally {
        setIsLoading(false);
      }
    };
    fetchSchedule();
  }, [playerId, t]);

  // Récupération des statistiques pour un match spécifique
  const fetchMatchStats = async (matchDate, opponent) => {
  const matchKey = `${matchDate}_${opponent}`;
  if (matchStats[matchKey]) return; // Déjà chargé
  
  setLoadingStats(true);
  try {
    console.log('Fetching stats for:', { matchDate, opponent }); // Log pour debug
    
    // Encoder les paramètres pour l'URL
    const encodedDate = encodeURIComponent(matchDate);
    const encodedOpponent = encodeURIComponent(opponent);
    
    const response = await api.get(
      `/api/player-stats/${playerId}/match/${encodedDate}/${encodedOpponent}`
    );
    
    setMatchStats(prev => ({
      ...prev,
      [matchKey]: response.data
    }));
  } catch (err) {
    console.error('Error al obtener estadísticas del partido:', err);
    // Optionnel : vous pouvez ajouter une gestion d'erreur spécifique
    if (err.response?.status === 404) {
      console.log('Match not found:', err.response.data);
    }
  } finally {
    setLoadingStats(false);
  }
};

  // Génère dynamiquement un mapping league => couleur en fonction des données
  const competitionColors = useMemo(() => {
    const leagues = Array.from(new Set(schedule.map(game => game.league)));
    const mapping = {};
    leagues.forEach((league, idx) => {
      mapping[league] = generateColor(idx, leagues.length);
    });
    return mapping;
  }, [schedule]);

  // Vérifie si deux dates (objets Date) représentent le même jour
  const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  // Retourne tous les matchs d'une date donnée
  const getMatchesByDate = (date) => {
    return schedule.filter((game) =>
      isSameDay(new Date(game.game_date), date)
    );
  };

  // Retourne true s'il y a au moins un match à la date donnée
  const hasGameOnDate = (date) => {
    return getMatchesByDate(date).length > 0;
  };

  // Ajoute une classe aux tuiles du calendrier si un match a lieu
  const tileClassName = ({ date, view }) => {
    if (view === 'month' && hasGameOnDate(date)) {
      return 'react-calendar__tile--with-match';
    }
    return null;
  };

  // Affiche, dans chaque tuile, de petites pastilles colorées pour chaque match ce jour-là
  const tileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    const matches = getMatchesByDate(date);
    if (matches.length === 0) return null;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 0.5 }}>
        {matches.map((game, idx) => {
          const color = competitionColors[game.league] || '#888';
          return (
            <Box
              key={idx}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: color,
                mb: 0.3
              }}
            />
          );
        })}
      </Box>
    );
  };

  // Gestion du clic sur une date
  const handleDateChange = (value) => {
  setSelectedDate(value);
  
  // Si c'est une date passée avec des matchs, charger les stats
  const matches = getMatchesByDate(value);
  const isPastDate = value < new Date();
  
  if (isPastDate && matches.length > 0) {
    matches.forEach(match => {
      // Créer une date locale pour éviter les problèmes de fuseau horaire
      const gameDate = new Date(match.game_date);
      const year = gameDate.getFullYear();
      const month = String(gameDate.getMonth() + 1).padStart(2, '0');
      const day = String(gameDate.getDate()).padStart(2, '0');
      const matchDate = `${year}-${month}-${day}`;
      
      fetchMatchStats(matchDate, match.opponent);
    });
  }
};

  // Liste des matchs du jour sélectionné
  const matchesForSelectedDate = getMatchesByDate(selectedDate);

  // Tri et sélection des 5 prochains matchs (dates futures)
  const upcomingMatches = schedule
    .filter((g) => new Date(g.game_date) >= new Date())
    .sort((a, b) => new Date(a.game_date) - new Date(b.game_date))
    .slice(0, 5);

  // Composant pour afficher les statistiques d'un match
  const MatchStatistics = ({ match }) => {
    const gameDate = new Date(match.game_date);
  const year = gameDate.getFullYear();
  const month = String(gameDate.getMonth() + 1).padStart(2, '0');
  const day = String(gameDate.getDate()).padStart(2, '0');
  const matchDate = `${year}-${month}-${day}`;
  
  const matchKey = `${matchDate}_${match.opponent}`;
  const stats = matchStats[matchKey];
    
    if (!stats && loadingStats) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    
    if (!stats) return null;
    
    // Fonction pour formater les statistiques de tir
    const formatShotStat = (statValue) => {
      if (!statValue || statValue === '-' || statValue === null) {
        return '0/0 (0%)';
      }
      
      // Si c'est au format "X-Y" (avec tiret)
      if (typeof statValue === 'string' && statValue.includes('-')) {
        const parts = statValue.split('-');
        if (parts.length === 2) {
          const made = parseInt(parts[0]) || 0;
          const attempted = parseInt(parts[1]) || 0;
          const percent = attempted > 0 ? ((made / attempted) * 100).toFixed(1) : 0;
          return `${made}/${attempted} (${percent}%)`;
        }
      }
      
      // Si c'est au format "X/Y" (avec slash)
      if (typeof statValue === 'string' && statValue.includes('/')) {
        const [made, attempted] = statValue.split('/').map(Number);
        const percent = attempted > 0 ? ((made / attempted) * 100).toFixed(1) : 0;
        return `${made}/${attempted} (${percent}%)`;
      }
      
      // Si c'est un nombre (pourcentage)
      const numValue = parseFloat(statValue);
      if (!isNaN(numValue)) {
        return `- (${numValue.toFixed(1)}%)`;
      }
      
      return statValue;
    };
    
    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell colSpan={2} align="center">
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('schedule.matchStatistics')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{t('schedule.minutes')}</TableCell>
              <TableCell align="right">{stats.minutes || '-'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('schedule.points')}</TableCell>
              <TableCell align="right">
                <strong>{stats.points || 0}</strong>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('schedule.twoPointShots')}</TableCell>
              <TableCell align="right">{formatShotStat(stats.two_fg_percent)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('schedule.threePointShots')}</TableCell>
              <TableCell align="right">{formatShotStat(stats.three_fg_percent)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('schedule.freeThrows')}</TableCell>
              <TableCell align="right">{formatShotStat(stats.free_throw_percent)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('schedule.rebounds')}</TableCell>
              <TableCell align="right">
                {stats.total_rebounds || 0} ({t('schedule.offensive')}: {stats.rebounds_off || 0}, {t('schedule.defensive')}: {stats.rebounds_def || 0})
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('schedule.assists')}</TableCell>
              <TableCell align="right">{stats.assists || 0}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('schedule.steals')}</TableCell>
              <TableCell align="right">{stats.steals || 0}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('schedule.blocks')}</TableCell>
              <TableCell align="right">{stats.blocks || 0}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('schedule.turnovers')}</TableCell>
              <TableCell align="right">{stats.turnovers || 0}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('schedule.fouls')}</TableCell>
              <TableCell align="right">{stats.personal_fouls || 0}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('schedule.rating')}</TableCell>
              <TableCell align="right">
                <Chip
                  label={stats.ranking || 0}
                  color={stats.ranking > 10 ? "success" : stats.ranking > 0 ? "warning" : "error"}
                  size="small"
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Composant pour afficher les informations des matchs
  const MatchesInfo = ({ matches, showTitle = true }) => {
    const isPastDate = selectedDate < new Date();
    
    if (matches.length === 0) {
      return (
        <Alert severity="info">{t('schedule.noMatchesForThisDate')}</Alert>
      );
    }

    return (
      <Box>
        {showTitle && (
          <Typography variant="h6" gutterBottom>
            {t('schedule.matchesFor')} {selectedDate?.toLocaleDateString(i18n.language)}
          </Typography>
        )}
        {matches.map((game, idx) => {
          const color = competitionColors[game.league] || '#888';
          return (
            <Card
              key={idx}
              variant="outlined"
              sx={{
                mb: 2,
                borderLeft: `6px solid ${color}`,
                boxShadow: '0 3px 6px rgba(0,0,0,0.1)'
              }}
            >
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color }}>
                  {t('schedule.competition')}: {game.league}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>{t('schedule.round')}:</strong> {game.round}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('schedule.location')}:</strong> {t(`schedule.locations.${game.location?.toLowerCase()}`, game.location)}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('schedule.opponent')}:</strong> {game.opponent}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('schedule.score')}:</strong> {game.boxscore || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('schedule.result')}:</strong> {game.result ? t(`schedule.results.${game.result.toLowerCase()}`, game.result) : '-'}
                </Typography>

                {/* Afficher les statistiques si c'est un match passé */}
                {isPastDate && <MatchStatistics match={game} />}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!schedule || schedule.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {t('schedule.noScheduleAvailable')}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Titre */}
      
      <Divider sx={{ mb: 2 }} />

      {/* Légende dynamique des couleurs par compétition */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
        {Object.entries(competitionColors).map(([comp, color]) => (
          <Box key={comp} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: color }} />
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {comp}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Layout principal */}
      <Grid container spacing={3}>
        {/* Calendrier à gauche */}
        <Grid item xs={12} lg={6}>
          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <Calendar
              locale={i18n.language}
              onChange={handleDateChange}
              value={selectedDate}
              tileClassName={tileClassName}
              tileContent={tileContent}
            />
          </Box>
          
          {/* Sur mobile/tablette, afficher les infos sous le calendrier */}
          {!isDesktop && (
            <Box sx={{ mt: 3 }}>
              <MatchesInfo matches={matchesForSelectedDate} />
            </Box>
          )}
        </Grid>
        
        {/* Informations des matchs à droite (desktop seulement) */}
        {isDesktop && (
          <Grid item xs={12} lg={6}>
            <Box sx={{ 
              position: 'sticky', 
              top: 20,
              maxHeight: 'calc(100vh - 100px)',
              overflowY: 'auto',
              pr: 1
            }}>
              <MatchesInfo matches={matchesForSelectedDate} />
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Card récapitulative des 5 prochains matchs */}
      <Box sx={{ mt: 5 }}>
        <Card variant="outlined" sx={{ boxShadow: '0 3px 6px rgba(0,0,0,0.1)', borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              {t('schedule.upcomingMatches', { count: 5 })}
            </Typography>
            {upcomingMatches.length === 0 ? (
              <Typography variant="body2">
                {t('schedule.noUpcomingMatches')}
              </Typography>
            ) : (
              upcomingMatches.map((match, idx) => {
                const color = competitionColors[match.league] || '#888';
                const dateString = new Date(match.game_date).toLocaleDateString(i18n.language);
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      mb: 2,
                      pl: 1,
                      borderLeft: `4px solid ${color}`
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color }}>
                      {match.league}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('schedule.date')}:</strong> {dateString}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('schedule.location')}:</strong> {t(`schedule.locations.${match.location?.toLowerCase()}`, match.location)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('schedule.opponent')}:</strong> {match.opponent}
                    </Typography>
                  </Box>
                );
              })
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ProfilSchedule;